import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Logo, GradientBar, Avatar, Badge, Button, PremiumBadge } from "@/components/design-system";
import { TID } from "@/lib/testIds";
import { api } from "@/lib/api";
import { unreadReportsCount } from "@/lib/aiReports";
import NotificationBell from "@/components/shell/NotificationBell";

const cn = (...xs) => xs.filter(Boolean).join(" ");

const NAV_BY_ROLE = {
  founder: [
    { to: "/app", label: "Overview", end: true },
    { to: "/app/products", label: "My products" },
    { to: "/app/campaigns", label: "Campaigns" },
    { to: "/app/reports", label: "Feedback reports" },
    { to: "/app/ambassadors", label: "Find ambassadors" },
    { to: "/app/payouts", label: "Payouts" },
    { to: "/app/how-it-works", label: "How does it work?" },
  ],
  community: [
    { to: "/app", label: "Overview", end: true },
    { to: "/app/explore", label: "Explore products" },
    { to: "/app/my-feedback", label: "My feedback" },
    { to: "/app/reputation", label: "Reputation" },
    { to: "/app/ambassadors", label: "Ambassador" },
    { to: "/app/how-it-works", label: "How does it work?" },
  ],
  ambassador: [
    { to: "/app", label: "Overview", end: true },
    { to: "/app/ambassadors", label: "My programme" },
    { to: "/app/referrals", label: "Referral links" },
    { to: "/app/payouts", label: "Earnings" },
  ],
  admin: [
    { to: "/app", label: "Overview", end: true },
    { to: "/app/admin/users", label: "Users" },
    { to: "/app/admin/moderation", label: "Moderation" },
    { to: "/app/admin/ambassadors", label: "Ambassadors" },
    { to: "/app/admin/referrals", label: "Referrals" },
    { to: "/app/admin/payouts", label: "Payouts" },
  ],
};

const ROLE_LABELS = {
  founder: "Founder",
  community: "Community",
  ambassador: "Ambassador",
  admin: "Administrator",
};

export default function AppShell() {
  const { user, logout, switchRole } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close mobile drawer on route change — hooks must be declared before any early return.
  React.useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  // Feedback-reports unread badge (founders only)
  const [unreadReports, setUnreadReports] = useState(0);
  const isFounder = user?.roles?.includes("founder");
  React.useEffect(() => {
    if (!isFounder) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try { const n = await unreadReportsCount(); if (!cancelled) setUnreadReports(n); }
      catch { /* silently ignore */ }
    };
    fetchCount();
    const handler = () => fetchCount();
    window.addEventListener("reports:unread-changed", handler);
    return () => { cancelled = true; window.removeEventListener("reports:unread-changed", handler); };
  }, [isFounder, loc.pathname]);

  if (!user) return null;

  const active = user.active_role || user.roles?.[0] || "community";
  const railItems = NAV_BY_ROLE[active] || NAV_BY_ROLE.community;
  const showVerifyBanner = !user.email_verified;

  const onSwitch = async (r) => { await switchRole(r); nav("/app"); setDrawerOpen(false); };
  const onLogout = async () => { await logout(); nav("/", { replace: true }); };
  const resendVerify = async () => { try { await api.post("/auth/resend-verification"); } catch { /* ignore */ } };

  return (
    <div className="min-h-screen bg-[#E8F1FF]">
      {/* Top nav */}
      <header data-testid={TID.shell.topNav} className="sticky top-0 z-40 bg-white border-b border-[#0B1F3A]/10">
        <div className="h-16 md:h-20 px-4 md:px-6 flex items-center justify-between max-w-[1600px] mx-auto gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              aria-label="Open navigation menu"
              data-testid="shell-mobile-menu-btn"
              onClick={() => setDrawerOpen(true)}
              className="md:hidden -ml-1 p-2 rounded-lg text-[#0B1F3A] hover:bg-[#E8F1FF]"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            </button>
            <Link to="/app" data-testid={TID.shell.topLogo} className="flex items-center gap-3 min-w-0">
              <Logo variant="light" height={40} className="md:!h-[54px]" />
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shell-main-page-link"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-[#0B1F3A]/75 hover:text-[#2D7FF9] hover:bg-[#E8F1FF] transition-colors"
              title="Open landing page"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 12l9-9 9 9" />
                <path d="M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10" />
              </svg>
              Main page
            </a>
            <NotificationBell />
            {user.roles?.length > 1 && (
              <div data-testid={TID.shell.roleSwitcher} className="hidden md:flex items-center gap-1 rounded-full border border-[#0B1F3A]/10 p-1 bg-[#E8F1FF]">
                {user.roles.map((r) => (
                  <button
                    key={r}
                    onClick={() => onSwitch(r)}
                    data-testid={`role-switch-${r}`}
                    className={cn("px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors",
                      r === active ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70 hover:text-[#0B1F3A]")}
                  >{ROLE_LABELS[r] || r}</button>
                ))}
              </div>
            )}
            <div className="relative">
              <button data-testid={TID.shell.avatarMenu} onClick={() => setMenuOpen((v) => !v)} className="flex items-center gap-2">
                <Avatar name={user.name} />
                <div className="hidden md:block text-left">
                  <div className="text-sm font-semibold text-[#0B1F3A] leading-tight flex items-center gap-2">
                    {user.name}
                    {user.plan === "premium" && <PremiumBadge />}
                  </div>
                  <div className="text-[11px] text-[#0B1F3A]/60 leading-tight">
                    {ROLE_LABELS[active]}
                    {typeof user.reputation_points === "number" && user.roles?.includes("community") && (
                      <> · <span className="font-bold text-[#2D7FF9]" data-testid="topnav-rep">{user.reputation_points} pts</span></>
                    )}
                  </div>
                </div>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-12 w-56 bg-white border border-[#0B1F3A]/10 rounded-xl shadow-lg py-2 z-50">
                  <div className="px-4 py-2 text-xs text-[#0B1F3A]/60">{user.email}</div>
                  <div className="px-4 py-2 flex flex-wrap gap-1">
                    {user.roles?.map((r) => (<Badge key={r} variant={r === "admin" ? "admin" : r === "founder" ? "founder" : r === "ambassador" ? "ambassador" : "community"}>{ROLE_LABELS[r]}</Badge>))}
                  </div>
                  <div className="border-t border-[#0B1F3A]/10 my-1" />
                  {(user.roles?.includes("founder") || user.roles?.includes("community")) && (
                    <Link to="/app/billing" onClick={() => setMenuOpen(false)} data-testid="account-menu-billing" className="block px-4 py-2 text-sm text-[#0B1F3A] hover:bg-[#E8F1FF]">Billing</Link>
                  )}
                  <Link to="/app/settings" onClick={() => setMenuOpen(false)} data-testid="account-menu-settings" className="block px-4 py-2 text-sm text-[#0B1F3A] hover:bg-[#E8F1FF]">Settings</Link>
                  <button data-testid={TID.shell.logout} onClick={onLogout} className="w-full text-left px-4 py-2 text-sm text-[#0B1F3A] hover:bg-[#E8F1FF]">Log out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="shell-mobile-drawer">
          <div
            className="absolute inset-0 bg-[#0B1F3A]/40 backdrop-blur-[2px]"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside className="absolute inset-y-0 left-0 w-[82%] max-w-xs bg-white shadow-2xl flex flex-col overflow-y-auto">
            <div className="h-16 px-4 flex items-center justify-between border-b border-[#0B1F3A]/10">
              <Logo variant="light" height={36} />
              <button
                type="button"
                aria-label="Close navigation menu"
                data-testid="shell-mobile-close-btn"
                onClick={() => setDrawerOpen(false)}
                className="p-2 rounded-lg text-[#0B1F3A] hover:bg-[#E8F1FF]"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
              </button>
            </div>
            <div className="px-4 py-4 border-b border-[#0B1F3A]/10 flex items-center gap-3">
              <Avatar name={user.name} />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#0B1F3A] truncate flex items-center gap-2">
                  {user.name}
                  {user.plan === "premium" && <PremiumBadge withStar={false} label="Prem" />}
                </div>
                <div className="text-[11px] text-[#0B1F3A]/60 truncate">
                  {ROLE_LABELS[active]}
                  {typeof user.reputation_points === "number" && user.roles?.includes("community") && (
                    <> · <span className="font-bold text-[#2D7FF9]">{user.reputation_points} pts</span></>
                  )}
                </div>
              </div>
            </div>
            {user.roles?.length > 1 && (
              <div className="px-4 pt-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#0B1F3A]/50 mb-2">Switch role</div>
                <div className="flex flex-wrap gap-1 rounded-full border border-[#0B1F3A]/10 p-1 bg-[#E8F1FF]">
                  {user.roles.map((r) => (
                    <button
                      key={r}
                      onClick={() => onSwitch(r)}
                      data-testid={`mobile-role-switch-${r}`}
                      className={cn("px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors",
                        r === active ? "bg-[#0B1F3A] text-white" : "text-[#0B1F3A]/70")}
                    >{ROLE_LABELS[r] || r}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="px-4 pt-6">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#0B1F3A]/50 mb-2">{ROLE_LABELS[active]} workspace</div>
              <nav className="flex flex-col gap-1">
                {railItems.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={`mobile-rail-${it.label.toLowerCase().replace(/\s+/g,'-')}`}
                    className={({isActive}) => cn(
                      "px-3 py-2.5 rounded-lg text-sm font-medium flex items-center justify-between gap-2",
                      isActive ? "bg-[#E8F1FF] text-[#2D7FF9]" : "text-[#0B1F3A]/85"
                    )}
                  >
                    <span>{it.label}</span>
                    {it.to === "/app/reports" && unreadReports > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-[#DC2626] text-white">
                        {unreadReports}
                      </span>
                    )}
                  </NavLink>
                ))}
              </nav>
            </div>
            <div className="mt-auto px-4 py-4 border-t border-[#0B1F3A]/10 space-y-1">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shell-mobile-main-page-link"
                className="block px-3 py-2 text-sm text-[#0B1F3A] rounded-lg hover:bg-[#E8F1FF]"
              >Main page ↗</a>
              {(user.roles?.includes("founder") || user.roles?.includes("community")) && (
                <Link to="/app/billing" className="block px-3 py-2 text-sm text-[#0B1F3A] rounded-lg hover:bg-[#E8F1FF]">Billing</Link>
              )}
              <Link to="/app/settings" className="block px-3 py-2 text-sm text-[#0B1F3A] rounded-lg hover:bg-[#E8F1FF]">Settings</Link>
              <button data-testid="shell-mobile-logout" onClick={onLogout} className="w-full text-left px-3 py-2 text-sm text-[#0B1F3A] rounded-lg hover:bg-[#E8F1FF]">Log out</button>
            </div>
          </aside>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-[240px_1fr]">
        {/* Left rail (desktop) */}
        <aside data-testid={TID.shell.leftRail} className="hidden md:flex flex-col bg-white border-r border-[#0B1F3A]/10 min-h-[calc(100vh-5rem)] py-6 px-3 sticky top-20 h-[calc(100vh-5rem)]">
          <div className="px-3">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#0B1F3A]/50 mb-3">{ROLE_LABELS[active]} workspace</div>
          </div>
          <nav className="flex flex-col gap-1">
            {railItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`rail-${it.label.toLowerCase().replace(/\s+/g,'-')}`}
                className={({isActive}) => cn(
                  "px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-between gap-2",
                  isActive ? "bg-[#E8F1FF] text-[#2D7FF9]" : "text-[#0B1F3A]/80 hover:bg-[#E8F1FF] hover:text-[#0B1F3A]"
                )}
              >
                <span>{it.label}</span>
                {it.to === "/app/reports" && unreadReports > 0 && (
                  <span
                    data-testid="rail-reports-unread-badge"
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-[#DC2626] text-white"
                  >{unreadReports}</span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto pt-6 border-t border-[#0B1F3A]/10">
            <div className="text-[11px] text-[#0B1F3A]/50 px-3">v0.1 · Cycle C1</div>
            <div className="text-[11px] text-[#0B1F3A]/50 px-3 mt-1">Validate. Launch. Grow.</div>
          </div>
        </aside>

        <main data-testid={TID.shell.main} className="p-4 md:p-10 min-h-[calc(100vh-5rem)]">
          {showVerifyBanner && (
            <div data-testid={TID.shell.verifyBanner} className="mb-6 rounded-xl bg-white border border-[#0B1F3A]/10 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
              <div>
                <p className="text-sm text-[#0B1F3A]"><b>Confirm your email</b> to unlock every feature.</p>
                <p className="text-xs text-[#0B1F3A]/60">Check your inbox for the verification link.</p>
              </div>
              <Button data-testid="resend-verify" variant="secondary" onClick={resendVerify}>Resend email</Button>
            </div>
          )}
          <Outlet />
          <footer data-testid={TID.shell.footer} className="mt-16 pt-6 border-t border-[#0B1F3A]/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 text-xs text-[#0B1F3A]/60">
            <span>© {new Date().getFullYear()} Pastoria Project SL · EU data residency · GDPR-first</span>
            <div className="flex gap-4">
              <Link to="/legal/privacy" className="hover:text-[#0B1F3A]">Privacy</Link>
              <Link to="/legal/terms" className="hover:text-[#0B1F3A]">Terms</Link>
              <Link to="/app/settings" className="hover:text-[#0B1F3A]">Data rights</Link>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
